import { Link, useLocation } from 'react-router-dom'
import { useLocale, useT } from '../i18n/LocaleContext'
import { LOCALES, LOCALE_META, LOCALE_PREFIX, type Locale } from '../i18n'
import { localizedPath, ROUTES, pageKeyForPath } from '../i18n/routes'
import { getArticleByLocaleSlug } from '../data/articles'
import { getSectorByLocaleSlug } from '../data/sectors'

/**
 * URL équivalente de la page courante dans la langue cible — dérivée du
 * `pathname`, PAS du contexte de route. Ce composant vit dans <Nav>, en dehors
 * de <Routes>, donc usePageKey()/useParams() y sont vides : on doit tout
 * recalculer depuis l'URL pour émettre de vrais liens <a> inter-langues côté
 * serveur (essentiel pour le maillage hreflang et le crawl des pages EN/NL).
 * - page statique connue → localizedPath(pageKey)
 * - article de blog / détail secteur → slug traduit de l'élément courant
 * - sinon (accueil ou page inconnue) → accueil de la langue cible
 */
function targetUrl(to: Locale, current: Locale, pathname: string): string {
  const staticKey = pageKeyForPath(pathname)
  if (staticKey) return localizedPath(staticKey, to)

  const bare = pathname.slice(LOCALE_PREFIX[current].length)

  const blogSeg = `/${ROUTES.blog[current]}/`
  if (bare.startsWith(blogSeg)) {
    const slug = bare.slice(blogSeg.length)
    if (slug && !slug.includes('/')) {
      const article = getArticleByLocaleSlug(current, slug)
      if (article) {
        return localizedPath('blog.article', to, { slug: article.i18n[to].slug })
      }
    }
  }

  const sectorSeg = `/${ROUTES.sectors[current]}/`
  if (bare.startsWith(sectorSeg)) {
    const slug = bare.slice(sectorSeg.length)
    if (slug && !slug.includes('/')) {
      const found = getSectorByLocaleSlug(current, slug)
      if (found) {
        return localizedPath('sectors.detail', to, { slug: found.base.slug[to] })
      }
    }
  }

  return localizedPath('home', to)
}

export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const current = useLocale()
  const { t } = useT()
  const { pathname } = useLocation()

  return (
    <div
      className={`flex items-center gap-0.5 ${compact ? '' : 'rounded-full bg-white/60 px-1 py-0.5'}`}
      role="group"
      aria-label={t('langSwitcher.label')}
    >
      {LOCALES.map((l) => {
        const active = l === current
        return (
          <Link
            key={l}
            to={targetUrl(l, current, pathname)}
            hrefLang={LOCALE_META[l].htmlLang}
            aria-current={active ? 'page' : undefined}
            className={`px-2 py-1 text-xs font-semibold uppercase rounded-full transition-colors ${
              active ? 'bg-ink-950 text-white' : 'text-ink-600 hover:text-ink-950 hover:bg-white/70'
            }`}
          >
            {l}
          </Link>
        )
      })}
    </div>
  )
}
